import { useEffect, useRef, KeyboardEvent as RKeyEvent } from 'react'
import { isInputDOMNode } from '../utils'

export interface KeyboardShortCutItem {
  shortcut?: {
    key: string
    meta?: boolean
    shift?: boolean
    ctrl?: boolean
    alt?: boolean
    option?: boolean
  }
  method?: () => boolean | void
  onSelect?: () => boolean | void
  onlyOnHover?: boolean
}

const getCallback = (item: KeyboardShortCutItem) => ({
  callback: item?.method || item?.onSelect,
  onlyOnHover: item.onlyOnHover || false,
})

export const useKeyboardShortcuts = (
  commands: KeyboardShortCutItem[],
  postMethodExecution?: (data: unknown) => void, // callback to open or close menus and other modals
) => {
  const commandObject = useRef<{
    [key: string]: {
      callback: (() => boolean | void) | undefined
      onlyOnHover: boolean
    }
  }>({})

  // Register commands
  useEffect(() => {
    const currentCommands = commandObject.current
    commands?.forEach((item) => {
      const callback = getCallback(item)
      if (!item.shortcut?.key || !callback.callback) return
      const shortcut = item.shortcut?.key.toLowerCase()
      if (item.shortcut?.meta) currentCommands[`${shortcut}+meta`] = callback
      else if (item.shortcut?.shift) currentCommands[`${shortcut}+shift`] = callback
      else if (item.shortcut?.ctrl) currentCommands[`${shortcut}+ctrl`] = callback
      else currentCommands[shortcut || ''] = callback
    })

    return () => {
      commands?.forEach((item) => {
        const shortcut = item.shortcut?.key.toLowerCase()
        if (item.shortcut?.meta) delete currentCommands[`${shortcut}+meta`]
        else if (item.shortcut?.shift) delete currentCommands[`${shortcut}+shift`]
        else delete currentCommands[shortcut || '']
      })
    }
  }, [commands])

  // Handle keydown events
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      const key = e.key?.toLowerCase()
      let method
      let isModifierLess = false

      if (e.metaKey || e.ctrlKey) method = commandObject.current[`${key}+meta`]
      else if (e.shiftKey) method = commandObject.current[`${key}+shift`]
      else {
        method = commandObject.current[key]
        isModifierLess = true
      }

      if (method?.callback) {
        if (isModifierLess && isInputDOMNode(e as unknown as RKeyEvent<HTMLSpanElement>)) return
        const isUndo = key === 'z' && (e.metaKey || e.ctrlKey)
        if (isUndo && isInputDOMNode(e as unknown as RKeyEvent<HTMLSpanElement>)) return

        if (method.onlyOnHover) {
          e.preventDefault()
          e.stopPropagation()
        }
        // Execute the callback for the shortcut
        const result = method.callback()
        if (postMethodExecution) postMethodExecution(result)
      }
    }

    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])
}
